import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { BaseStitchListComponent, StitchListItem } from '@modules/common/components/list/base-stitch-list.component';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { type StitchCardSelection, type StitchCardVariant } from '@modules/common/utils/stitch-card-variant';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { NotebookComponent } from '@modules/elements/components/notebook/notebook.component';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { NotebooksService } from '@modules/notes/services/notebooks.service';
import { NotesAppStateService } from '@modules/notes/services/state.service';
import { NotesTenantScopeService } from '@modules/notes/services/notes-tenant-scope.service';
import { Note } from '@modules/notes/types/note';
import { Notebook } from '@modules/notes/types/notebook';
import { NotebooksFilters } from '@modules/notes/types/notebooks-filters';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-notebooks-list',
  templateUrl: './notebooks-list.component.html',
  styleUrls: ['./notebooks-list.component.less'],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: false,
})
export class NotebooksListComponent extends BaseStitchListComponent<Notebook> implements OnInit, OnDestroy, OnChanges {
  public readonly tenantItems = input<ReadonlyArray<{ readonly accountId?: string }>>();

  /** Show each row's hover quick actions; off for lists that only preview their items. */
  public readonly actionsButtonEnabled = input<boolean>(true);

  /** Container treatment forwarded to every card; the knowledge panel asks for `'card'`. */
  public readonly variant = input<StitchCardVariant>('row');

  /** Id of the item open in the dynamic panel, forwarded so that card draws itself as selected. */
  public readonly previewedItemId = input<string | null>(null);

  /** What each card draws as selected, forwarded; the knowledge panel asks for the list's own selection. */
  public readonly cardSelection = input<StitchCardSelection>('preview');

  /** The knowledge panel draws its results as cards; every other caller keeps the app's flush row. */
  protected readonly isCard = computed(() => this.variant() === 'card');

  @Input()
  public override filters: Partial<NotebooksFilters> = {};
  @Input()
  public withTags = false;
  @Input()
  public withKnots = false;
  @Input()
  public knotsSource: KnotSource;
  @Input()
  public debug: 'score';

  @Output()
  public readonly clickNotebook = new EventEmitter<Notebook>();
  @Output()
  public readonly openNotebook = new EventEmitter<Notebook>();
  @Output()
  public readonly doubleClickNotebook = new EventEmitter<Notebook>();
  @Output()
  public readonly openNote = new EventEmitter<Note>();

  @ViewChildren(NotebookComponent, { read: ElementRef<HTMLElement> })
  public override itemElements: QueryList<ElementRef<HTMLElement>>;

  public override items: StitchListItem<Notebook>[] = [];

  private readonly notebooksService = inject(NotebooksService);
  private readonly notesStateService = inject(NotesAppStateService);
  private readonly linkedInfoService = inject(LinkedInfoService);
  private readonly notesTenantScopeService = inject(NotesTenantScopeService, { optional: true });

  protected override stitchSignalSpaceIds(): ReadonlyArray<string> {
    const contextId = this.spaceContextStore.contextId();
    const effectiveTenantId = this.notesTenantScopeService?.effectiveTenantId();
    const spaceIds = new Set<string>([contextId]);
    if (effectiveTenantId) {
      spaceIds.add(effectiveTenantId);
    }
    for (const { accountId } of this.tenantItems() ?? []) {
      if (accountId) {
        spaceIds.add(accountId);
      }
    }
    return [...spaceIds];
  }

  protected override getStitchType(): StitchType {
    return StitchType.NOTEBOOK;
  }

  public override ngOnInit() {
    merge(
      // Global refresh button
      this.notesStateService.getRefreshAll(),
      // Subscribe for linked info updates
      this.linkedInfoService.getRefresh(),
      this.notebooksService.getRefresh(),
    )
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadPage(this.page);
      });

    super.ngOnInit();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('filters' in changes) {
      this.resetItems();
    }
  }

  protected override getItems(offset: number, limit: number): Observable<{ items: Notebook[]; count: number }> {
    return this.notebooksService.search(
      { ...this.filters, limit, offset },
      {
        withTags: this.withTags,
        withKnots: this.withKnots,
        withConnections: false,
        knotsSource: this.knotsSource,
        tenantItems: this.tenantItems(),
      },
    );
  }

  public handleDoubleClickItem(notebook: Notebook) {
    this.doubleClickNotebook.emit(notebook);
  }

  public handleOpenSelected(event: SelectableOpenEvent<Notebook>): void {
    this.clickNotebook.emit(event.item.data);
  }
}
